import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, CalendarCheck, BedDouble, Receipt, Pill, Users, Stethoscope, BarChart3,
  ClipboardList, Baby, FileText, ShieldCheck, Lock, KeyRound, Building2, FileClock,
  Smartphone, Mail, Phone, MapPin, Clock, Menu, X, Check, FlaskConical, ShieldHalf,
} from "lucide-react";
import { Logo, LogoMark, WhatsAppIcon, WHATSAPP_LINK, CONTACT } from "@/components/Brand";
import { useForceLightSurface } from "@/context/ThemeContext";

const NAV = [
  { label: "Product", href: "#product" },
  { label: "Modules", href: "#modules" },
  { label: "Security", href: "#security" },
  { label: "Contact", href: "#contact" },
];

const HIGHLIGHTS = [
  { icon: Building2, title: "Multi-tenant", body: "Every hospital and pharmacy is a fully isolated tenant with its own UHIDs, invoice series and staff." },
  { icon: ShieldCheck, title: "Secure by default", body: "Role-based permissions, session control and an immutable audit trail behind every clinical action." },
  { icon: Smartphone, title: "Mobile-ready", body: "Reception, wards and pharmacy counters run the same workflows on a phone, tablet or desktop." },
  { icon: ClipboardList, title: "OPD + IPD", body: "Token queues, consultations, admissions, bed maps, clinical notes and discharge — one continuous record." },
  { icon: Receipt, title: "Billing that reconciles", body: "Multi-line invoices with server-computed totals, part payments and audited voids. The bill never disagrees." },
  { icon: FileText, title: "Clinical management", body: "Prescriptions, case sheets, discharge summaries and maternity records, all print-ready on A4." },
];

const MODULES = [
  { icon: CalendarCheck, title: "Appointments", body: "Book a slot and register a brand-new patient in the same window. Doctor slots are protected against overlaps." },
  { icon: Users, title: "Patients", body: "Tenant-unique UHIDs with validated Aadhaar, PAN, passport and voter ID, plus structured addresses." },
  { icon: ClipboardList, title: "OPD", body: "Daily token queues, consultation capture and instant prescription printing." },
  { icon: BedDouble, title: "IPD & beds", body: "Live bed map, admissions, treatment charges and automatic bed release on discharge." },
  { icon: Stethoscope, title: "Doctors & staff", body: "Departments, schedules, registration numbers and per-doctor consultation fees." },
  { icon: Receipt, title: "Billing", body: "Consultation, OPD, IPD, procedure and service invoices with GST and payment tracking." },
  { icon: Pill, title: "Pharmacy", body: "POS counter, batch and expiry tracking, purchases, suppliers, returns and stock movement." },
  { icon: BarChart3, title: "Reports", body: "Revenue, collections, clinical volumes and patient-origin analytics for any date range." },
];

const CLINICAL = [
  { icon: BedDouble, title: "IPD case management", body: "Admission details, vitals, progress and nursing notes, treatment charges and a printable A4 case sheet.", live: true },
  { icon: FileText, title: "Prescriptions", body: "Structured medicines with strength, dosage, frequency, duration and instructions — one document system shared by OPD and IPD.", live: true },
  { icon: Baby, title: "Maternity & obstetrics", body: "Antenatal profile, ANC visits, investigations, labour, delivery, newborn linkage, postpartum and discharge.", live: true },
  { icon: FlaskConical, title: "Laboratory", body: "Test ordering, result entry and reports attached to the patient's clinical record.", live: false },
  { icon: ShieldHalf, title: "Insurance & TPA", body: "Claim tracking against IPD bills with approval and reimbursement status.", live: false },
];

const SHOWCASE = [
  { src: "appointments", label: "Appointments", body: "Reception books a slot and registers a new patient without switching screens." },
  { src: "opd", label: "OPD queue", body: "Token-wise queue with consultation and prescription in a single flow." },
  { src: "casesheet", label: "IPD case sheet", body: "The complete inpatient record, formatted for A4 and ready to print." },
  { src: "invoice", label: "Invoice preview", body: "Branded tax invoice with line-level GST, discounts and payment status." },
  { src: "attendance", label: "Staff attendance", body: "A daily register — pick a date, check the team in or out, mark leave." },
  { src: "maternity", label: "Maternity", body: "Pregnancy to delivery to discharge, with the newborn linked to the mother." },
  { src: "reports", label: "Reports & analytics", body: "Revenue, collections and where your patients actually come from." },
  { src: "patients", label: "Patient records", body: "Validated identities, structured addresses and full visit history." },
];

const SECURITY = [
  { icon: KeyRound, title: "Multi-factor sign-in", body: "Email and password or Google identity, with verified email and one-time password resets." },
  { icon: Lock, title: "Session locking", body: "Server-side sessions with httpOnly cookies, revocation and brute-force protection on login." },
  { icon: ShieldCheck, title: "Role-based access", body: "Granular permissions per module and per action, plus custom roles for your own job titles." },
  { icon: Building2, title: "Tenant isolation", body: "Every query is scoped to the signed-in business. Nothing is ever read from the request body." },
  { icon: FileClock, title: "Audit logs", body: "Who did what, when and from where — retained for clinical and financial accountability." },
  { icon: MapPin, title: "Built for India", body: "Aadhaar, PAN, GSTIN and drug-licence validation, IST reporting and INR billing throughout." },
];

const WHY = [
  { title: "Reception is the bottleneck, so we fixed reception", body: "Registering a walk-in and booking their doctor happens in one window. No second screen, no re-typing, no lost patients at the desk." },
  { title: "Bills that survive an audit", body: "Line totals, tax and grand totals are calculated on the server. Invoices are never deleted — a correction is a void with a reason and an audit entry." },
  { title: "Identity checked before the record exists", body: "Aadhaar checksum, PAN structure, passport by country, country-aware phone numbers. Duplicate mobiles and IDs are blocked in the database, not just the form." },
  { title: "One clinical record, not five modules", body: "An appointment becomes an OPD visit, an admission, a case sheet, a prescription and a discharge summary — carrying the same patient forward." },
  { title: "Maternity built in, not bolted on", body: "A full obstetrics pathway inside IPD, with the newborn getting its own UHID while staying linked to the mother's case." },
  { title: "Works on the phone in the ward", body: "Tables become readable cards, actions stay reachable and forms are comfortable on a touch screen." },
];

const useReveal = () => {
  useEffect(() => {
    const els = document.querySelectorAll(".reveal");
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) { e.target.classList.add("in"); io.unobserve(e.target); } });
    }, { threshold: 0.12 });
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);
};

const Shot = ({ name, alt, className = "", eager = false }) => (
  <div className={`shot-frame ${className}`}>
    <img src={`/screens/${name}.jpg`} alt={alt} width="1800" height="1085"
      loading={eager ? "eager" : "lazy"} decoding="async"
      className="block w-full h-auto" />
  </div>
);

const Eyebrow = ({ children }) => (
  <p className="overline text-white/45">{children}</p>
);

const SectionHead = ({ eyebrow, title, body, className = "" }) => (
  <div className={`reveal max-w-3xl ${className}`}>
    <Eyebrow>{eyebrow}</Eyebrow>
    <h2 className="mt-4 font-ui text-3xl sm:text-4xl lg:text-[44px] font-extrabold tracking-[-0.02em] leading-[1.08] text-white">
      {title}
    </h2>
    {body && <p className="mt-5 text-base text-white/60 leading-relaxed">{body}</p>}
  </div>
);

const WhatsAppButton = ({ className = "", label = "Chat on WhatsApp", testid = "whatsapp-btn" }) => (
  <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" data-testid={testid}
    className={`inline-flex items-center justify-center gap-2.5 rounded-full bg-[#25D366] px-7 py-3.5 text-[15px] font-semibold text-[#062314] transition hover:brightness-110 ${className}`}>
    <WhatsAppIcon className="h-5 w-5" /> {label}
  </a>
);

export default function Landing() {
  useForceLightSurface();
  useReveal();
  const [menu, setMenu] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const shotRef = useRef(null);
  const [active, setActive] = useState(0);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (!menu) return;
    const y = window.scrollY;
    const body = document.body;
    const prev = { position: body.style.position, top: body.style.top, width: body.style.width };
    body.style.position = "fixed";
    body.style.top = `-${y}px`;
    body.style.width = "100%";
    return () => {
      Object.assign(body.style, prev);
      window.scrollTo(0, y);
    };
  }, [menu]);

  return (
    <div className="min-h-screen ink text-white antialiased overflow-x-hidden" data-testid="landing-page">
      {/* ---------------- nav ---------------- */}
      <header className={`fixed inset-x-0 top-0 z-50 transition-colors duration-300 ${
        scrolled ? "bg-[#08090C]/85 backdrop-blur-xl border-b border-white/10" : "border-b border-transparent"}`}>
        <div className="mx-auto max-w-[1240px] px-5 sm:px-8 h-16 sm:h-[72px] flex items-center justify-between gap-4">
          <Link to="/" aria-label="AJ Classic Healthcare home"><Logo tone="dark" markClass="h-8 w-8 sm:h-9 sm:w-9" /></Link>
          <nav className="hidden lg:flex items-center gap-9">
            {NAV.map((n) => (
              <a key={n.href} href={n.href} data-testid={`nav-${n.label.toLowerCase()}`}
                className="text-[14px] font-medium text-white/60 transition hover:text-white">{n.label}</a>
            ))}
          </nav>
          <div className="hidden lg:flex items-center gap-3">
            <Link to="/login" data-testid="nav-login-btn"
              className="rounded-full border border-white/20 px-5 py-2 text-[14px] font-medium text-white transition hover:border-white/60">
              Sign in
            </Link>
            <Link to="/login" data-testid="nav-get-started-btn"
              className="rounded-full bg-white px-5 py-2 text-[14px] font-semibold text-[#08090C] transition hover:brightness-90">
              Get started
            </Link>
          </div>
          <button className="lg:hidden -mr-1 p-2" onClick={() => setMenu(true)} aria-label="Open menu" data-testid="mobile-menu-btn">
            <Menu className="h-6 w-6" />
          </button>
        </div>
      </header>

      {menu && (
        <div className="fixed inset-0 z-[60] bg-[#08090C] lg:hidden" data-testid="mobile-menu">
          <div className="px-5 h-16 flex items-center justify-between border-b border-white/10">
            <Logo tone="dark" markClass="h-8 w-8" />
            <button onClick={() => setMenu(false)} aria-label="Close menu" data-testid="mobile-menu-close" className="p-2">
              <X className="h-6 w-6" />
            </button>
          </div>
          <nav className="px-5 py-8 flex flex-col gap-2">
            {NAV.map((n) => (
              <a key={n.href} href={n.href} onClick={() => setMenu(false)}
                className="rounded-xl px-4 py-4 text-lg font-semibold text-white/80 hover:bg-white/5">{n.label}</a>
            ))}
            <Link to="/login" onClick={() => setMenu(false)} className="mt-4 btn-white w-full" data-testid="mobile-login-btn">
              Sign in <ArrowRight className="h-4 w-4" />
            </Link>
            <WhatsAppButton className="w-full mt-3" testid="mobile-whatsapp-btn" />
          </nav>
        </div>
      )}

      {/* ---------------- hero ---------------- */}
      <section className="relative pt-28 sm:pt-36 pb-16 sm:pb-24">
        <div className="absolute inset-0 hair-dark opacity-60" aria-hidden="true" />
        <div className="absolute inset-x-0 top-0 h-[520px] beam" aria-hidden="true" />
        <div className="relative mx-auto max-w-[1240px] px-5 sm:px-8">
          <div className="max-w-[880px]">
            <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-1.5 text-[12px] font-medium text-white/70">
              <span className="h-1.5 w-1.5 rounded-full bg-[#25D366]" />
              Multi-tenant HMS · Hospitals & pharmacies · India
            </span>
            <h1 className="mt-7 font-ui text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-[-0.03em] leading-[1.03]">
              The hospital management system
              <span className="block text-white/45">your front desk can actually keep up with.</span>
            </h1>
            <p className="mt-7 max-w-2xl text-base sm:text-lg text-white/60 leading-relaxed">
              AJ Classic Healthcare runs OPD and IPD, appointments, clinical records, pharmacy and billing
              on one secure, multi-tenant platform — with validated patient identities, print-ready
              clinical documents and an audit trail behind every action.
            </p>
            <div className="mt-9 flex flex-col sm:flex-row gap-3.5">
              <Link to="/login" className="btn-white" data-testid="hero-get-started-btn">
                Get started <ArrowRight className="h-4 w-4" />
              </Link>
              <a href="#product" className="btn-outline-white" data-testid="hero-product-btn">See the product</a>
            </div>
            <div className="mt-9 flex flex-wrap gap-x-7 gap-y-3 text-[13px] text-white/45">
              {["Tenant-isolated data", "Role-based access", "Audit-logged", "Mobile-first"].map((t) => (
                <span key={t} className="inline-flex items-center gap-2"><Check className="h-3.5 w-3.5 text-white/70" />{t}</span>
              ))}
            </div>
          </div>

          <div className="relative mt-16 sm:mt-20 shot-glow reveal" ref={shotRef}>
            <Shot name="dashboard" eager className="shot-tilt"
              alt="AJ Classic Healthcare hospital dashboard showing today's appointments, OPD queue, bed occupancy and revenue" />
            <p className="mt-6 text-center text-[12px] text-white/35">
              Live hospital workspace — actual product interface, not a mockup.
            </p>
          </div>
        </div>
      </section>

      {/* ---------------- highlights ---------------- */}
      <section className="py-20 sm:py-28 ink-2 border-y border-white/10">
        <div className="mx-auto max-w-[1240px] px-5 sm:px-8">
          <SectionHead eyebrow="Product highlights"
            title="Built around how a hospital actually runs."
            body="Not a generic admin panel with a stethoscope icon. Every module exists because a real desk, ward or counter needed it." />
          <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {HIGHLIGHTS.map((h, i) => (
              <div key={h.title} className="mono-card glow-soft p-7 reveal" style={{ transitionDelay: `${i * 60}ms` }}
                data-testid={`highlight-${i}`}>
                <h.icon className="h-5 w-5 text-white" strokeWidth={1.6} />
                <h3 className="mt-5 font-ui text-base md:text-lg font-bold tracking-tight">{h.title}</h3>
                <p className="mt-3 text-sm text-white/55 leading-relaxed">{h.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ---------------- dashboard showcase ---------------- */}
      <section id="product" className="py-20 sm:py-28 scroll-mt-20">
        <div className="mx-auto max-w-[1240px] px-5 sm:px-8">
          <div className="grid gap-14 lg:grid-cols-[0.9fr_1.35fr] lg:items-center">
            <div>
              <SectionHead eyebrow="The workspace"
                title="One dashboard, the whole hospital day."
                body="Sign in and the day is already laid out: appointments due, the OPD queue, beds occupied, pending collections and low-stock medicines. From there every workflow is two clicks away — register a patient, admit them, prescribe, bill, discharge." />
              <ul className="mt-9 space-y-4">
                {["Appointment → OPD visit → admission → discharge, on one record",
                  "Permissions decide what each role even sees",
                  "Every figure traces back to an auditable document"].map((t) => (
                  <li key={t} className="flex gap-3 text-sm text-white/65">
                    <Check className="h-4 w-4 shrink-0 text-white mt-0.5" /> {t}
                  </li>
                ))}
              </ul>
            </div>
            <div className="shot-glow reveal">
              <Shot name="ipd" alt="AJ Classic Healthcare IPD and bed management screen with live bed map and admissions" />
            </div>
          </div>
        </div>
      </section>

      {/* ---------------- core modules ---------------- */}
      <section id="modules" className="py-20 sm:py-28 ink-2 border-y border-white/10 scroll-mt-20">
        <div className="mx-auto max-w-[1240px] px-5 sm:px-8">
          <SectionHead eyebrow="Core modules" title="Everything the hospital and the pharmacy need." />
          <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {MODULES.map((m, i) => (
              <div key={m.title} className="mono-card glow-soft p-6 reveal" style={{ transitionDelay: `${i * 45}ms` }}
                data-testid={`module-${i}`}>
                <m.icon className="h-5 w-5 text-white" strokeWidth={1.6} />
                <h3 className="mt-5 font-ui text-base font-bold tracking-tight">{m.title}</h3>
                <p className="mt-2.5 text-[13px] text-white/55 leading-relaxed">{m.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ---------------- clinical modules ---------------- */}
      <section className="py-20 sm:py-28">
        <div className="mx-auto max-w-[1240px] px-5 sm:px-8">
          <SectionHead eyebrow="Clinical & specialised"
            title="Clinical depth, printed the way hospitals need it."
            body="Case sheets, prescriptions and discharge summaries are A4-ready documents with a preview, print and cancel flow — not screenshots of a web page." />
          <div className="mt-14 grid gap-5 lg:grid-cols-3">
            {CLINICAL.map((c, i) => (
              <div key={c.title} className={`mono-card p-7 reveal ${c.live ? "glow-soft" : "opacity-70"}`}
                style={{ transitionDelay: `${i * 60}ms` }} data-testid={`clinical-${i}`}>
                <div className="flex items-start justify-between gap-4">
                  <c.icon className="h-5 w-5 text-white" strokeWidth={1.6} />
                  <span className={`rounded-full border px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${
                    c.live ? "border-white/25 text-white/80" : "border-white/15 text-white/40"}`}>
                    {c.live ? "Available" : "On the roadmap"}
                  </span>
                </div>
                <h3 className="mt-5 font-ui text-base md:text-lg font-bold tracking-tight">{c.title}</h3>
                <p className="mt-3 text-sm text-white/55 leading-relaxed">{c.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ---------------- real screenshots ---------------- */}
      <section className="py-20 sm:py-28 ink-2 border-y border-white/10">
        <div className="mx-auto max-w-[1240px] px-5 sm:px-8">
          <SectionHead eyebrow="Inside the product"
            title="Real screens from the running system."
            body="No stock photography and no invented dashboards. Everything below is captured from AJ Classic Healthcare itself." />
          <div className="mt-12 -mx-5 px-5 sm:mx-0 sm:px-0 flex gap-2.5 overflow-x-auto pb-3 sm:flex-wrap sm:overflow-visible">
            {SHOWCASE.map((s, i) => (
              <button key={s.src} onClick={() => setActive(i)} data-testid={`showcase-tab-${i}`}
                className={`shrink-0 rounded-full border px-5 py-2 text-[13px] font-medium transition ${
                  active === i ? "border-white bg-white text-[#08090C]" : "border-white/15 text-white/60 hover:border-white/45 hover:text-white"}`}>
                {s.label}
              </button>
            ))}
          </div>
          <div className="mt-8 shot-glow reveal" data-testid="showcase-frame">
            <Shot name={SHOWCASE[active].src} alt={`AJ Classic Healthcare — ${SHOWCASE[active].label}`} />
          </div>
          <p className="mt-6 max-w-2xl text-sm text-white/55">{SHOWCASE[active].body}</p>
        </div>
      </section>

      {/* ---------------- security ---------------- */}
      <section id="security" className="py-20 sm:py-28 scroll-mt-20">
        <div className="mx-auto max-w-[1240px] px-5 sm:px-8">
          <SectionHead eyebrow="Security & compliance"
            title="Patient data is the product. We treat it that way."
            body="Access control, tenant isolation and audit logging are structural, not settings you remember to switch on." />
          <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {SECURITY.map((s, i) => (
              <div key={s.title} className="mono-card p-7 reveal" style={{ transitionDelay: `${i * 50}ms` }}
                data-testid={`security-${i}`}>
                <s.icon className="h-5 w-5 text-white" strokeWidth={1.6} />
                <h3 className="mt-5 font-ui text-base md:text-lg font-bold tracking-tight">{s.title}</h3>
                <p className="mt-3 text-sm text-white/55 leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>
          <p className="mt-8 text-xs text-white/35 max-w-3xl">
            Identity fields are validated for format, checksum and duplication. Verification against a
            government or GST authority requires a separate authorised integration and is not implied here.
          </p>
        </div>
      </section>

      {/* ---------------- why AJ Classic ---------------- */}
      <section className="py-20 sm:py-28 ink-2 border-y border-white/10">
        <div className="mx-auto max-w-[1240px] px-5 sm:px-8">
          <SectionHead eyebrow="Why AJ Classic Healthcare"
            title="Chosen for the boring parts that go wrong everywhere else." />
          <div className="mt-14 grid gap-5 md:grid-cols-2">
            {WHY.map((w, i) => (
              <div key={w.title} className="mono-card glow-soft p-7 sm:p-8 reveal" style={{ transitionDelay: `${i * 50}ms` }}
                data-testid={`why-${i}`}>
                <span className="font-mono-num text-[11px] text-white/35">{String(i + 1).padStart(2, "0")}</span>
                <h3 className="mt-4 font-ui text-base md:text-lg font-bold tracking-tight">{w.title}</h3>
                <p className="mt-3 text-sm text-white/55 leading-relaxed">{w.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ---------------- mobile experience ---------------- */}
      <section className="py-20 sm:py-28">
        <div className="mx-auto max-w-[1240px] px-5 sm:px-8">
          <div className="grid gap-14 lg:grid-cols-[1fr_1.1fr] lg:items-center">
            <div>
              <SectionHead eyebrow="Mobile experience"
                title="The ward round happens on a phone."
                body="Tables become readable cards instead of shrinking into nothing. Check-in, check-out, mark leave, print and collect payment stay one tap away. Forms use real touch targets, searchable dropdowns and country-code phone pickers." />
              <div className="mt-9 grid gap-3 sm:grid-cols-3">
                {["Phone", "Tablet", "Desktop"].map((d) => (
                  <div key={d} className="mono-card px-5 py-4 text-sm text-white/70">{d}</div>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3 sm:gap-5 reveal">
              {["mobile-dashboard", "mobile-attendance", "mobile-appointments"].map((n, i) => (
                <div key={n} className={`shot-frame ${i === 1 ? "sm:-translate-y-6" : ""}`}>
                  <img src={`/screens/${n}.jpg`} alt={`AJ Classic Healthcare on mobile — ${n.replace("mobile-", "")}`}
                    width="900" height="1870" loading="lazy" decoding="async" className="block w-full h-auto" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ---------------- contact ---------------- */}
      <section id="contact" className="py-20 sm:py-28 ink-2 border-t border-white/10 scroll-mt-20">
        <div className="mx-auto max-w-[1240px] px-5 sm:px-8">
          <div className="grid gap-12 lg:grid-cols-[1.05fr_1fr] lg:items-start">
            <div>
              <SectionHead eyebrow="Talk to us" title="Book a walkthrough of the live system."
                body="Tell us how your hospital or pharmacy runs today and we will show you the exact screens your team would use." />
              <div className="mt-9 flex flex-col sm:flex-row gap-3.5">
                <WhatsAppButton testid="contact-whatsapp-btn" />
                <a href={`mailto:${CONTACT.email}?subject=AJ%20Classic%20HMS%20enquiry`}
                  className="btn-outline-white" data-testid="contact-email-btn">
                  <Mail className="h-4 w-4" /> Email us
                </a>
              </div>
              <p className="mt-5 text-xs text-white/35 max-w-lg">
                WhatsApp here is a direct customer-contact chat for software enquiries — it is not the
                HMS transactional messaging integration.
              </p>
            </div>
            <div className="mono-card p-7 sm:p-8 reveal" data-testid="contact-card">
              <dl className="space-y-6">
                {[
                  { icon: Mail, label: "Email", value: CONTACT.email, href: `mailto:${CONTACT.email}`, testid: "contact-email-link" },
                  { icon: Phone, label: "Software contact", value: CONTACT.phone, href: CONTACT.phoneHref, testid: "contact-phone-link" },
                  { icon: MapPin, label: "Office", value: CONTACT.office },
                  { icon: Clock, label: "Office hours", value: CONTACT.hours },
                ].map((row) => (
                  <div key={row.label} className="flex gap-4">
                    <row.icon className="h-4 w-4 mt-1 shrink-0 text-white/50" strokeWidth={1.7} />
                    <div>
                      <dt className="overline text-white/40">{row.label}</dt>
                      <dd className="mt-1.5 text-[15px] font-medium break-words">
                        {row.href ? (
                          <a href={row.href} data-testid={row.testid} className="underline decoration-white/25 underline-offset-4 hover:decoration-white">
                            {row.value}
                          </a>
                        ) : row.value}
                      </dd>
                    </div>
                  </div>
                ))}
              </dl>
            </div>
          </div>
        </div>
      </section>

      {/* ---------------- footer ---------------- */}
      <footer className="border-t border-white/10 py-14 sm:py-16">
        <div className="mx-auto max-w-[1240px] px-5 sm:px-8">
          <div className="grid gap-10 sm:grid-cols-2 lg:grid-cols-4">
            <div>
              <Logo tone="dark" />
              <p className="mt-5 max-w-xs text-sm text-white/45 leading-relaxed">
                A secure, multi-tenant hospital and pharmacy management system built for Indian healthcare providers.
              </p>
              <WhatsAppButton className="mt-6 !px-5 !py-2.5 !text-[13px]" label="WhatsApp us" testid="footer-whatsapp-btn" />
            </div>
            <div>
              <p className="overline text-white/40">Product</p>
              <ul className="mt-5 space-y-3 text-sm text-white/55">
                {NAV.map((n) => <li key={n.href}><a href={n.href} className="hover:text-white">{n.label}</a></li>)}
                <li><Link to="/login" className="hover:text-white">Sign in</Link></li>
              </ul>
            </div>
            <div>
              <p className="overline text-white/40">Legal</p>
              <ul className="mt-5 space-y-3 text-sm text-white/55">
                <li><Link to="/privacy" className="hover:text-white" data-testid="footer-privacy-link">Privacy Policy</Link></li>
                <li><Link to="/terms" className="hover:text-white" data-testid="footer-terms-link">Terms &amp; Conditions</Link></li>
              </ul>
            </div>
            <div>
              <p className="overline text-white/40">Contact</p>
              <ul className="mt-5 space-y-3 text-sm text-white/55">
                <li><a href={`mailto:${CONTACT.email}`} className="hover:text-white break-all">{CONTACT.email}</a></li>
                <li><a href={CONTACT.phoneHref} className="hover:text-white">{CONTACT.phone}</a></li>
                <li>{CONTACT.office}</li>
                <li>{CONTACT.hours}</li>
              </ul>
            </div>
          </div>
          <div className="rule-fade my-10" />
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 text-xs text-white/35">
            <p>© {new Date().getFullYear()} AJ Classic Healthcare. All rights reserved.</p>
            <p className="inline-flex items-center gap-2"><LogoMark className="h-4 w-4" rounded="rounded-[4px]" /> Hospital Management System</p>
          </div>
        </div>
      </footer>

      {/* ---------------- floating WhatsApp ---------------- */}
      <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" data-testid="floating-whatsapp-btn"
        aria-label="Chat with AJ Classic on WhatsApp"
        className="fixed bottom-5 right-5 z-50 grid h-14 w-14 place-items-center rounded-full bg-[#25D366] text-[#062314] shadow-[0_10px_40px_-8px_rgba(37,211,102,.6)] transition hover:scale-105">
        <WhatsAppIcon className="h-7 w-7" />
      </a>
    </div>
  );
}
